import { type ReactNode } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

type StripeAccountStatus = 'none' | 'pending' | 'verified' | 'restricted'

interface PaymentsProps {
  stripe_account_status: StripeAccountStatus
  has_account: boolean
}

export default function PaymentsShow({ stripe_account_status, has_account }: PaymentsProps) {
  const isVerified = stripe_account_status === 'verified'
  const isPending = stripe_account_status === 'pending'
  const isRestricted = stripe_account_status === 'restricted'

  const cardTitle = isVerified
    ? 'Payments connected'
    : isRestricted
      ? 'Payments restricted'
      : isPending
        ? 'Verification in progress'
        : 'Set up payments'

  const cardDescription = isVerified
    ? 'Your Stripe account is verified and ready to accept ticket sales and donations.'
    : isRestricted
      ? 'Stripe has restricted your account. Resume onboarding to provide additional information.'
      : isPending
        ? 'Stripe is reviewing your information, or you still have onboarding to finish.'
        : 'Sell tickets and accept donations on your events. Funds settle directly to your bank. Gala Season takes 0%; Stripe processing fees apply.'

  const badgeLabel = isVerified ? 'Verified' : isRestricted ? 'Restricted' : isPending ? 'Pending' : 'Not set up'
  const badgeVariant = isVerified ? 'default' : isRestricted ? 'destructive' : isPending ? 'outline' : 'secondary'

  return (
    <>
      <Head title="Payments" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-4xl font-semibold tracking-tight">Payments</h1>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{cardTitle}</CardTitle>
                <CardDescription>{cardDescription}</CardDescription>
              </div>
              <Badge variant={badgeVariant}>{badgeLabel}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {isRestricted && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <p className="text-base text-destructive">
                  Your Stripe account is restricted. Resume onboarding to provide the required information.
                </p>
              </div>
            )}

            {isPending && (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3">
                <p className="text-base">
                  We&apos;re waiting for Stripe to confirm your account. If you didn&apos;t complete the form, resume below.
                </p>
              </div>
            )}

            {!has_account && !isVerified && (
              <div className="space-y-3">
                <p className="text-base font-medium">What happens after you set up:</p>
                <ul className="space-y-2">
                  {[
                    'Tickets, sponsorships, and donations are sold through your event pages',
                    'Funds go straight to your Stripe balance (2-day rolling payouts to your bank)',
                    'Buyers see the Stripe-hosted checkout — your branding, no card data on Gala Season',
                    'You issue refunds from your dashboard; Stripe sends 1099-Ks if applicable'
                  ].map(text => (
                    <li key={text} className="flex items-start gap-2 text-base text-muted-foreground">
                      <svg
                        className="mt-1 h-4 w-4 shrink-0 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isVerified ? (
              <Button
                variant="outline"
                onClick={() => router.post('/dashboard/settings/payments/create_login_link')}
              >
                Open Stripe dashboard
              </Button>
            ) : has_account ? (
              <Button onClick={() => router.post('/dashboard/settings/payments/create_account_link')}>
                Resume onboarding
              </Button>
            ) : (
              <Button onClick={() => router.post('/dashboard/settings/payments/create_account')}>
                Set up payments
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

PaymentsShow.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
